import React, { memo, useState, useEffect } from 'react';
import { Globe, ArrowUpRight, ArrowDownRight, ChevronLeft, ChevronRight, Scan, Sparkles, CheckSquare, Calculator, Settings } from 'lucide-react';
import { useAppStore } from '../store';
import { formatPrice, formatPercent, formatMoney } from '../utils/helpers';
import { useTranslation } from '../utils/translations';

const Sidebar = memo(function Sidebar() {
  const { t } = useTranslation();
  const { prices, selectedPair, setSelectedPair, accountInfo, activeTab, setActiveTab } = useAppStore();
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Navigation menu items
  const navItems = [
    { id: 'scanner', label: t('scanner'), icon: Scan },
    { id: 'ai-analysis', label: 'AI Analysis', icon: Sparkles },
    { id: 'checklist', label: t('checklist'), icon: CheckSquare },
    { id: 'strategies', label: 'Strategies', icon: Settings },
    { id: 'calculator', label: 'Calculator', icon: Calculator },
  ];

  // Default pairs if prices not loaded
  const defaultPairs = [
    { symbol: 'EURUSD', bid: 1.0892, change: 0.23, trend: 'up' },
    { symbol: 'GBPUSD', bid: 1.2634, change: -0.15, trend: 'down' },
    { symbol: 'USDJPY', bid: 149.82, change: 0.45, trend: 'up' },
    { symbol: 'USDCHF', bid: 0.8765, change: -0.12, trend: 'down' },
    { symbol: 'AUDUSD', bid: 0.6543, change: 0.18, trend: 'up' },
    { symbol: 'USDCAD', bid: 1.3654, change: 0.22, trend: 'up' },
    { symbol: 'NZDUSD', bid: 0.6123, change: -0.08, trend: 'down' },
    { symbol: 'EURGBP', bid: 0.8623, change: 0.11, trend: 'up' },
    { symbol: 'EURJPY', bid: 163.25, change: 0.34, trend: 'up' },
    { symbol: 'GBPJPY', bid: 189.34, change: 0.56, trend: 'up' },
    { symbol: 'AUDJPY', bid: 98.12, change: -0.23, trend: 'down' },
    { symbol: 'EURAUD', bid: 1.6654, change: 0.15, trend: 'up' },
    { symbol: 'XAUUSD', bid: 2043.50, change: 1.12, trend: 'up' },
    { symbol: 'XAGUSD', bid: 24.35, change: -0.32, trend: 'down' },
    { symbol: 'US30', bid: 38245.00, change: 0.67, trend: 'up' },
    { symbol: 'US100', bid: 17892.50, change: 0.89, trend: 'up' },
    { symbol: 'US500', bid: 5034.25, change: 0.45, trend: 'up' },
    { symbol: 'GER40', bid: 17234.50, change: 0.33, trend: 'up' },
    { symbol: 'UK100', bid: 7654.30, change: -0.21, trend: 'down' },
    { symbol: 'JPN225', bid: 38234.00, change: 0.78, trend: 'up' },
    { symbol: 'USOIL', bid: 78.45, change: 1.23, trend: 'up' },
    { symbol: 'BTCUSD', bid: 43567.80, change: 2.34, trend: 'up' },
  ];

  const displayPairs = prices.length > 0 ? prices : defaultPairs;

  // Top pairs for carousel - mostra tutte le valute disponibili, ordinate per popolarità
  // Se abbiamo prices reali, usiamo quelli, altrimenti usiamo tutti i defaultPairs
  const topPairs = displayPairs.length > 0 
    ? displayPairs.slice(0, Math.min(displayPairs.length, 20)) // Mostra fino a 20 valute
    : defaultPairs.slice(0, 20);

  // Auto-rotate carousel solo se ci sono più di 1 valuta
  useEffect(() => {
    if (topPairs.length <= 1) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % topPairs.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [topPairs.length]);

  return (
    <div className="hidden lg:block w-64 flex-shrink-0">
      <div className="glass-card p-5 sticky top-24">
        {/* Carousel Valute in Alto */}
        {topPairs.length > 0 && (
          <div className="mb-5 relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Markets
              </h3>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-500">
                  {carouselIndex + 1}/{topPairs.length}
                </span>
                <div className="flex items-center gap-1">
                  {topPairs.slice(0, Math.min(topPairs.length, 10)).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCarouselIndex(idx)}
                      className={`h-1.5 rounded-full transition-smooth ${
                        idx === carouselIndex ? 'bg-violet-400 w-4' : 'bg-slate-600 w-1.5'
                      }`}
                    />
                  ))}
                  {topPairs.length > 10 && (
                    <span className="text-[10px] text-slate-500 ml-1">...</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/70 to-slate-900/70 border border-slate-700/60 shadow-medium">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
              >
                {topPairs.map((pair, idx) => (
                  <div key={idx} className="min-w-full p-4">
                    <button
                      onClick={() => setSelectedPair(pair.symbol)}
                      className={`w-full text-left transition-smooth active:scale-[0.98] ${
                        selectedPair === pair.symbol ? 'opacity-100' : 'opacity-90 hover:opacity-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {pair.change >= 0 || pair.trend === 'up' ? (
                            <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <ArrowDownRight className="w-5 h-5 text-red-400" />
                          )}
                          <span className="font-bold text-white text-base">
                            {pair.symbol.replace('USD', '/USD').replace('EUR', 'EUR/').replace('GBP', 'GBP/').replace('JPY', '/JPY')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <div className="text-2xl font-bold text-white font-mono">
                          {formatPrice(pair.bid || pair.price, pair.symbol)}
                        </div>
                        <div className={`text-sm font-bold px-2 py-1 rounded ${
                          (pair.change >= 0 || pair.trend === 'up') 
                            ? 'text-emerald-400 bg-emerald-400/10' 
                            : 'text-red-400 bg-red-400/10'
                        }`}>
                          {formatPercent(pair.change)}
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Navigation Arrows */}
              {topPairs.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIndex((prev) => (prev - 1 + topPairs.length) % topPairs.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-slate-800/90 hover:bg-slate-700/90 rounded-full transition-smooth active:scale-95 shadow-soft backdrop-blur-sm border border-slate-700/50"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => setCarouselIndex((prev) => (prev + 1) % topPairs.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-slate-800/90 hover:bg-slate-700/90 rounded-full transition-smooth active:scale-95 shadow-soft backdrop-blur-sm border border-slate-700/50"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-cyan-500/10">
              <Globe className="w-4 h-4 text-cyan-400" />
            </div>
            <span className="tracking-wide">{t('watch_list')}</span>
          </h3>
          <span className="text-xs text-emerald-300 font-medium flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400/50" />
            {t('live')}
          </span>
        </div>

        {/* Pairs List */}
        <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
          {displayPairs.map((pair) => (
            <button
              key={pair.symbol}
              onClick={() => setSelectedPair(pair.symbol)}
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-smooth active:scale-[0.98] ${selectedPair === pair.symbol
                ? 'bg-violet-500/20 border border-violet-500/50 shadow-soft shadow-violet-500/10'
                : 'bg-slate-800/40 border border-slate-700/30 hover:bg-slate-800/60 hover:border-slate-600/40'
                }`}
            >
              <div className="flex items-center gap-2.5">
                {pair.change >= 0 || pair.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-400 flex-shrink-0" />
                )}
                <span className="font-semibold text-white text-sm tracking-tight">
                  {pair.symbol.replace('USD', '/USD').replace('EUR', 'EUR/').replace('GBP', 'GBP/').replace('JPY', '/JPY')}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xs text-white font-mono font-medium">
                  {formatPrice(pair.bid || pair.price, pair.symbol)}
                </div>
                <div
                  className={`text-xs font-semibold ${(pair.change >= 0 || pair.trend === 'up') ? 'text-emerald-300' : 'text-red-300'
                    }`}
                >
                  {formatPercent(pair.change)}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Navigation Menu */}
        <div className="mt-5 pt-5 border-t border-slate-700/50">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" />
            Navigation
          </h3>
          <div className="space-y-1.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl transition-smooth text-left ${
                    isActive
                      ? 'bg-violet-500/20 border border-violet-500/50 text-violet-300 shadow-soft shadow-violet-500/10'
                      : 'bg-slate-800/40 border border-slate-700/30 hover:bg-slate-800/60 hover:border-slate-600/40 text-slate-300'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-violet-400' : 'text-slate-400'}`} />
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800/40 rounded-lg p-2 text-center">
              <div className="text-xs text-slate-500">{t('today')}</div>
              <div className="text-sm font-bold text-emerald-400">
                {formatMoney(accountInfo?.profit || 2400, { compact: true, showSign: true })}
              </div>
            </div>
            <div className="bg-slate-800/40 rounded-lg p-2 text-center">
              <div className="text-xs text-slate-500">{t('open')}</div>
              <div className="text-sm font-bold text-white">2</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Sidebar;
